/**
 * Registro IVA — acquisti, vendite e liquidazione periodica.
 *
 * Mostra le fatture raggruppate per periodo con dettaglio aliquote IVA,
 * totali e calcolo liquidazione (IVA vendite - IVA acquisti).
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';

/* ------------------------------------------------------------------ */
/* Tipi                                                                */
/* ------------------------------------------------------------------ */

interface RigaAliquota {
  aliquota: number;
  natura: string | null;
  imponibile: number;
  iva: number;
}

interface RigaRegistro {
  id: string;
  data: string;
  numero: string | null;
  tipo_documento: string;
  soggetto: string;
  piva: string;
  importo_imponibile: number;
  importo_iva: number;
  importo_totale: number;
  importo_ritenuta: number;
  importo_bollo: number;
  split_payment: boolean;
  aliquote: RigaAliquota[];
}

interface Liquidazione {
  iva_vendite: number;
  iva_acquisti: number;
  saldo: number;
}

/* ------------------------------------------------------------------ */
/* Costanti                                                            */
/* ------------------------------------------------------------------ */

const ANNO_CORRENTE = new Date().getFullYear();

const MESI = [
  { value: '__tutto__', label: "Tutto l'anno" },
  { value: '1', label: 'Gennaio' },
  { value: '2', label: 'Febbraio' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Aprile' },
  { value: '5', label: 'Maggio' },
  { value: '6', label: 'Giugno' },
  { value: '7', label: 'Luglio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Settembre' },
  { value: '10', label: 'Ottobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Dicembre' },
];

type TabRegistro = 'acquisti' | 'vendite';

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const formatEUR = (value: number): string =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);

const buildParams = (anno: number, mese: string): URLSearchParams => {
  const params = new URLSearchParams({ anno: String(anno) });
  if (mese !== '__tutto__') params.set('mese', mese);
  return params;
};

/* ------------------------------------------------------------------ */
/* Componente principale                                               */
/* ------------------------------------------------------------------ */

const RegistroIva: React.FC = () => {
  const [tab, setTab] = useState<TabRegistro>('acquisti');
  const [anno, setAnno] = useState<number>(ANNO_CORRENTE);
  const [mese, setMese] = useState<string>('__tutto__');

  const [righeAcquisti, setRigheAcquisti] = useState<RigaRegistro[]>([]);
  const [righeVendite, setRigheVendite] = useState<RigaRegistro[]>([]);
  const [liquidazione, setLiquidazione] = useState<Liquidazione | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Righe espanse per mostrare dettaglio aliquote
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* ---------------------------------------------------------------- */
  /* Fetch dati                                                        */
  /* ---------------------------------------------------------------- */

  const caricaDati = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded(new Set());

    const qs = buildParams(anno, mese).toString();

    try {
      const [resAcq, resVen, resLiq] = await Promise.all([
        fetch(`/api/v1/registro-iva/acquisti?${qs}`, { headers: authHeaders() }),
        fetch(`/api/v1/registro-iva/vendite?${qs}`, { headers: authHeaders() }),
        fetch(`/api/v1/registro-iva/liquidazione?${qs}`, { headers: authHeaders() }),
      ]);

      if (!resAcq.ok) throw new Error(`Errore acquisti: ${resAcq.status}`);
      if (!resVen.ok) throw new Error(`Errore vendite: ${resVen.status}`);
      if (!resLiq.ok) throw new Error(`Errore liquidazione: ${resLiq.status}`);

      const [dataAcq, dataVen, dataLiq] = await Promise.all([
        resAcq.json() as Promise<RigaRegistro[]>,
        resVen.json() as Promise<RigaRegistro[]>,
        resLiq.json() as Promise<Liquidazione>,
      ]);

      setRigheAcquisti(dataAcq);
      setRigheVendite(dataVen);
      setLiquidazione(dataLiq);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [anno, mese]);

  useEffect(() => {
    caricaDati();
  }, [caricaDati]);

  /* ---------------------------------------------------------------- */
  /* Righe attive e totali                                             */
  /* ---------------------------------------------------------------- */

  const righe = tab === 'acquisti' ? righeAcquisti : righeVendite;

  const totaleImponibile = righe.reduce((s, r) => s + r.importo_imponibile, 0);
  const totaleIva = righe.reduce((s, r) => s + r.importo_iva, 0);
  const totaleDocumento = righe.reduce((s, r) => s + r.importo_totale, 0);

  /* ---------------------------------------------------------------- */
  /* Export URL                                                        */
  /* ---------------------------------------------------------------- */

  const exportUrl = `/api/v1/export/riepilogo-iva?${buildParams(anno, mese).toString()}`;

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */

  return (
    <div className="space-y-6 p-6">
      {/* Intestazione */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Registro IVA</h1>
        <a href={exportUrl} target="_blank" rel="noopener noreferrer">
          <Button variant="outline">Esporta riepilogo</Button>
        </a>
      </div>

      {/* Filtri periodo */}
      <Card>
        <CardContent className="flex items-end gap-4 pt-6">
          <div className="space-y-1">
            <Label>Anno</Label>
            <Input
              type="number"
              value={anno}
              onChange={(e) => setAnno(Number(e.target.value))}
              className="w-28"
              min={2000}
              max={2099}
            />
          </div>
          <div className="space-y-1">
            <Label>Mese</Label>
            <Select value={mese} onValueChange={setMese}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MESI.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tab Acquisti / Vendite */}
      <div className="flex gap-2">
        <Button
          variant={tab === 'acquisti' ? 'default' : 'outline'}
          onClick={() => setTab('acquisti')}
        >
          Acquisti
        </Button>
        <Button
          variant={tab === 'vendite' ? 'default' : 'outline'}
          onClick={() => setTab('vendite')}
        >
          Vendite
        </Button>
      </div>

      {/* Errore */}
      {error && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="pt-6 text-red-700">{error}</CardContent>
        </Card>
      )}

      {/* Tabella registro */}
      <Card>
        <CardHeader>
          <CardTitle>
            Registro {tab === 'acquisti' ? 'Acquisti' : 'Vendite'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground py-8 text-center">
              Caricamento...
            </p>
          ) : righe.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center">
              Nessuna fattura nel periodo selezionato.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Data</TableHead>
                  <TableHead>Numero</TableHead>
                  <TableHead>Soggetto</TableHead>
                  <TableHead>P.IVA</TableHead>
                  <TableHead className="text-right">Imponibile</TableHead>
                  <TableHead className="text-right">IVA</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {righe.map((riga) => (
                  <React.Fragment key={riga.id}>
                    {/* Riga fattura */}
                    <TableRow
                      className="cursor-pointer"
                      onClick={() => toggleExpanded(riga.id)}
                    >
                      <TableCell className="text-center text-xs">
                        {expanded.has(riga.id) ? '\u25BC' : '\u25B6'}
                      </TableCell>
                      <TableCell>
                        {new Date(riga.data).toLocaleDateString('it-IT')}
                      </TableCell>
                      <TableCell>{riga.numero}</TableCell>
                      <TableCell>{riga.soggetto}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {riga.piva}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatEUR(riga.importo_imponibile)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatEUR(riga.importo_iva)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatEUR(riga.importo_totale)}
                      </TableCell>
                    </TableRow>

                    {/* Dettaglio aliquote (righe espanse) */}
                    {expanded.has(riga.id) &&
                      riga.aliquote.map((aliq, idx) => (
                        <TableRow
                          key={`${riga.id}-aliq-${idx}`}
                          className="bg-muted/40"
                        >
                          <TableCell />
                          <TableCell
                            colSpan={4}
                            className="pl-8 text-sm italic"
                          >
                            Aliquota {aliq.aliquota}%
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {formatEUR(aliq.imponibile)}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {formatEUR(aliq.iva)}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      ))}
                  </React.Fragment>
                ))}

                {/* Riga totali */}
                <TableRow className="border-t-2 font-bold">
                  <TableCell />
                  <TableCell colSpan={4}>Totale</TableCell>
                  <TableCell className="text-right">
                    {formatEUR(totaleImponibile)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatEUR(totaleIva)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatEUR(totaleDocumento)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Liquidazione IVA */}
      {liquidazione && !loading && (
        <Card>
          <CardHeader>
            <CardTitle>Liquidazione IVA</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">IVA Vendite</p>
                <p className="text-lg font-semibold">
                  {formatEUR(liquidazione.iva_vendite)}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">IVA Acquisti</p>
                <p className="text-lg font-semibold">
                  {formatEUR(liquidazione.iva_acquisti)}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">
                  Saldo{' '}
                  {liquidazione.saldo >= 0 ? '(da versare)' : '(a credito)'}
                </p>
                <p
                  className={`text-xl font-bold ${
                    liquidazione.saldo >= 0 ? 'text-red-600' : 'text-green-600'
                  }`}
                >
                  {formatEUR(Math.abs(liquidazione.saldo))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RegistroIva;
